import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import {
  AssessmentOutlined as AssessmentOutlinedIcon,
  EmojiEventsOutlined as EmojiEventsOutlinedIcon,
  EventAvailableOutlined as EventAvailableOutlinedIcon,
  PaymentsOutlined as PaymentsOutlinedIcon,
  SchoolRounded as SchoolRoundedIcon,
  TodayOutlined as TodayOutlinedIcon,
} from '@mui/icons-material';
import axiosInstance from '../../api/axiosConfig';
import PageContainer from '../common/PageContainer';
import PageHeader from '../common/PageHeader';
import EmptyState from '../common/EmptyState';
import { STORAGE_KEYS } from '../../constants';
import { EmployeeProfileTableSection } from './EmployeeProfileSections';

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return 'N/A';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
};

const StatCard = ({ icon, label, value, helper, color }) => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: 1.5,
      height: '100%',
      backgroundColor: (theme) => alpha(color || theme.palette.primary.main, 0.05),
    }}
  >
    <CardContent>
      <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(color || '#2563eb', 0.12),
            color: color || '#2563eb',
          }}
        >
          {icon}
        </Box>
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>
      </Stack>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.75 }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {helper}
      </Typography>
    </CardContent>
  </Card>
);

const TeacherStatistics = () => {
  const { employeeId, employeId } = useParams();
  const selectedEmployeeId = employeeId || employeId;
  const navigate = useNavigate();
  const schoolId = localStorage.getItem(STORAGE_KEYS.SCHOOL_ID);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchStatistics = useCallback(async () => {
    if (!selectedEmployeeId) {
      setErrorMessage('Teacher ID is missing.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage('');
    try {
      const response = await axiosInstance.get(`/api/teacher-statistics/${selectedEmployeeId}`, {
        headers: schoolId ? { schoolId } : {},
      });
      setData(response?.data || null);
    } catch (error) {
      setData(null);
      setErrorMessage(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Unable to load teacher statistics right now.'
      );
    } finally {
      setLoading(false);
    }
  }, [schoolId, selectedEmployeeId]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  const teacher = data?.teacher || null;
  const attendanceSummary = data?.attendanceSummary || {};
  const payrollSummary = data?.payrollSummary || {};
  const leaveSummary = data?.leaveSummary || {};
  const profileSummary = data?.profileSummary || {};
  const academicPerformanceSummary = data?.academicPerformanceSummary || {};
  const assignmentPerformance = Array.isArray(data?.assignmentPerformance) ? data.assignmentPerformance : [];
  const achievements = Array.isArray(data?.achievements) ? data.achievements : [];
  const roleLabel = Array.isArray(teacher?.role) ? teacher.role.join(', ') : teacher?.role || 'N/A';
  const isTeacherRole = String(roleLabel).toLowerCase().includes('teacher');

  const employeeProfileRows = useMemo(() => ([
    { label: 'Employee Code', value: teacher?.employeeCode },
    { label: 'Full Name', value: `${teacher?.firstName || ''} ${teacher?.lastName || ''}`.trim() },
    { label: 'Role', value: roleLabel },
    { label: 'Designation', value: profileSummary.designation || teacher?.designation },
    { label: 'Phone', value: teacher?.phone },
    { label: 'Email', value: teacher?.email },
    { label: 'Gender', value: teacher?.gender },
    { label: 'Qualification', value: teacher?.qualification },
    { label: 'Experience', value: teacher?.experience != null ? `${teacher.experience} years` : 'N/A' },
    { label: 'Subjects Taught', value: profileSummary.subjectsTaught || teacher?.subjectsTaught },
    { label: 'Date of Joining', value: formatDate(profileSummary.dateOfJoining || teacher?.dateOfJoining) },
    { label: 'Status', value: profileSummary.status || teacher?.status },
  ]), [profileSummary.dateOfJoining, profileSummary.designation, profileSummary.status, profileSummary.subjectsTaught, roleLabel, teacher]);

  const quickCards = [
    {
      label: 'Attendance',
      value: attendanceSummary.attendancePercentage != null ? `${attendanceSummary.attendancePercentage}%` : 'N/A',
      helper: `${attendanceSummary.presentDays || 0} present and ${attendanceSummary.absentDays || 0} absent in ${attendanceSummary.window || 'recent records'}`,
      icon: <EventAvailableOutlinedIcon fontSize="small" />,
      color: '#2563eb',
    },
    {
      label: 'Payroll',
      value: payrollSummary.latestTotalSalary ? formatCurrency(payrollSummary.latestTotalSalary) : formatCurrency(payrollSummary.salary),
      helper: payrollSummary.latestPayPeriod ? `Latest payslip: ${payrollSummary.latestPayPeriod}` : 'No payslip generated yet',
      icon: <PaymentsOutlinedIcon fontSize="small" />,
      color: '#0f9d7a',
    },
    {
      label: 'Leave Balance',
      value: leaveSummary.remainingLeaves != null ? leaveSummary.remainingLeaves : 'N/A',
      helper: `${leaveSummary.usedLeaves || 0} used out of ${leaveSummary.allocatedLeaves || 0} allocated`,
      icon: <TodayOutlinedIcon fontSize="small" />,
      color: '#b7791f',
    },
    {
      label: 'Pass Percentage',
      value: academicPerformanceSummary.passPercentage != null ? `${academicPerformanceSummary.passPercentage}%` : 'N/A',
      helper: `${academicPerformanceSummary.passedStudents || 0} passed out of ${academicPerformanceSummary.evaluatedStudents || 0} evaluated`,
      icon: <SchoolRoundedIcon fontSize="small" />,
      color: '#dc2626',
    },
    {
      label: 'Achievements',
      value: achievements.length,
      helper: achievements.length ? 'Recognition records available' : 'No achievements recorded yet',
      icon: <EmojiEventsOutlinedIcon fontSize="small" />,
      color: '#7c3aed',
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!teacher) {
    return (
      <PageContainer>
        <EmptyState
          title="Teacher statistics unavailable"
          description={errorMessage || 'The requested teacher could not be loaded.'}
          action={<Button variant="outlined" onClick={() => navigate('/employees')}>Back to Employees</Button>}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={`${teacher.firstName || ''} ${teacher.lastName || ''}`.trim() || 'Employee Statistics'}
        subtitle={`${profileSummary.designation || teacher.designation || 'Employee'} | ${profileSummary.role || teacher.role || 'Staff profile'}`}
        actions={<Button variant="outlined" onClick={() => navigate(`/employee/${selectedEmployeeId}`)}>View Profile</Button>}
      />

      <Paper
        variant="outlined"
        sx={{
          p: 2.25,
          mb: 2,
          borderRadius: 1.5,
          background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.success.light, 0.15)} 100%)`,
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Employee statistics summary
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 760 }}>
              Review attendance, payroll readiness, leave usage, and achievements in one simple screen. This page is connected to the dedicated statistics controller so the profile and statistics view stay aligned.
            </Typography>
          </Box>
          <Chip
            label={roleLabel}
            color={isTeacherRole ? 'primary' : 'default'}
            variant={isTeacherRole ? 'filled' : 'outlined'}
            sx={{ fontWeight: 700, borderRadius: 2 }}
          />
        </Stack>
      </Paper>

      <Grid container spacing={2}>
        {quickCards.map((card) => (
          <Grid item xs={12} sm={6} xl={3} key={card.label}>
            <StatCard {...card} />
          </Grid>
        ))}
      </Grid>

      {isTeacherRole ? (
        <Box sx={{ mt: 2 }}>
          <EmployeeProfileTableSection rows={employeeProfileRows} />
        </Box>
      ) : null}

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} lg={7}>
          <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 1.5, height: '100%' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <AssessmentOutlinedIcon fontSize="small" color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Work Snapshot
              </Typography>
            </Stack>
            <Grid container spacing={2}>
                {[
                  { label: 'Subjects Taught', value: profileSummary.subjectsTaught || teacher.subjectsTaught || 'N/A' },
                  { label: 'Experience', value: teacher.experience != null ? `${teacher.experience} years` : 'N/A' },
                  { label: 'Date of Joining', value: formatDate(profileSummary.dateOfJoining || teacher.dateOfJoining) },
                  { label: 'Status', value: profileSummary.status || teacher.status || 'N/A' },
                  { label: 'Payslips', value: payrollSummary.payslipCount ?? 0 },
                  { label: 'Attendance Entries', value: attendanceSummary.entries ?? 0 },
                  { label: 'Assigned Subjects', value: academicPerformanceSummary.assignedSubjects ?? 0 },
                  { label: 'Classes Covered', value: academicPerformanceSummary.classesCovered ?? 0 },
                ].map((row) => (
                <Grid item xs={12} sm={6} key={row.label}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {row.label}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.4 }}>
                      {row.value}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 1.5, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5 }}>
              Recognition & Leave
            </Typography>
            <Stack spacing={1.25}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Achievements
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  {achievements.length
                    ? achievements.slice(0, 6).map((item) => (
                      <Chip
                        key={item.id || `${item.title}-${item.achievementDate || ''}`}
                        label={item.title || item.name || 'Achievement'}
                        color="primary"
                        variant="outlined"
                      />
                    ))
                    : <Chip label="No achievements recorded" variant="outlined" />}
                </Stack>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Leave Position
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {leaveSummary.balanceCount
                    ? `${leaveSummary.remainingLeaves || 0} leaves remain after using ${leaveSummary.usedLeaves || 0}.`
                    : 'No leave balance entries are available for the current year.'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Exam Performance
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {academicPerformanceSummary.passPercentage != null
                    ? `${academicPerformanceSummary.passPercentage}% pass rate across ${academicPerformanceSummary.examCount || 0} exam schedules in ${academicPerformanceSummary.academicYear || 'the current year'}.`
                    : 'No exam performance is available yet for the currently assigned subjects.'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Payroll Readiness
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {payrollSummary.latestPayPeriod
                    ? `Latest payroll run is available for ${payrollSummary.latestPayPeriod}.`
                    : 'Payslip history has not been generated yet for this teacher.'}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {isTeacherRole ? (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <SchoolRoundedIcon fontSize="small" color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Subject-wise Exam Performance
                </Typography>
              </Stack>
              {assignmentPerformance.length ? (
                <Grid container spacing={1.5}>
                  {assignmentPerformance.map((item) => (
                    <Grid item xs={12} md={6} xl={4} key={`${item.assignmentId || 'assignment'}-${item.subjectId}-${item.classId}-${item.sectionId}`}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 1.5,
                          bgcolor: (theme) => alpha(theme.palette.error.main, 0.03),
                          height: '100%',
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                          {item.subjectName || 'Subject'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.1 }}>
                          {(item.className || 'Class')} - {(item.sectionName || 'Section')}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                          <Chip
                            label={item.passPercentage != null ? `${item.passPercentage}% pass` : 'No results'}
                            color={item.passPercentage != null && item.passPercentage >= 60 ? 'success' : item.passPercentage != null ? 'warning' : 'default'}
                            size="small"
                            variant={item.passPercentage != null ? 'filled' : 'outlined'}
                          />
                          <Chip
                            label={item.averagePercentage != null ? `${item.averagePercentage}% avg` : 'Avg N/A'}
                            size="small"
                            variant="outlined"
                          />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {item.evaluatedStudents || 0} evaluated, {item.passedStudents || 0} passed, {item.failedStudents || 0} failed across {item.examCount || 0} exam rows.
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No teacher-subject exam performance is available yet. Assign subjects to the teacher and enter marks to start tracking pass percentage here.
                </Typography>
              )}
            </Paper>
          </Grid>
        ) : null}
      </Grid>
    </PageContainer>
  );
};

export default TeacherStatistics;
